import type { PartiallyRequired } from '@skedulo/horizon-types'
import {
  Actions,
  ErrorMiddleware,
  DevToolsRpcMiddleware,
} from '@skedulo/protobuf-web'
import {
  ActionTask,
  ActionTaskInput,
  IApiClientWithSessionOptions,
} from '../types'
import { FetchApiClient } from './fetch-api-client'

export type IGetActionExecutionRequest = Actions.API.GetActionExecutionRequest
export type IGetActionExecutionResponse = Actions.API.ActionExecution

export type IExecuteActionRequest = PartiallyRequired<
  Actions.API.ExecuteActionRequest,
  'actionId'
>
export type IExecuteActionResponse = Actions.API.ActionExecution

export class ActionsRpcClientV1 {
  readonly client: Actions.API.Client

  constructor({ platformApiDomain, metadata }: IApiClientWithSessionOptions) {
    this.client = new Actions.API.Client(
      new ErrorMiddleware(
        new DevToolsRpcMiddleware(
          new Actions.API.RPC(platformApiDomain, { metadata }),
        ),
      ),
    )
  }

  async getExecution(
    request: IGetActionExecutionRequest,
  ): Promise<IGetActionExecutionResponse> {
    const response = await this.client.getActionExecution(request)
    return response
  }

  async execute(
    request: IExecuteActionRequest,
  ): Promise<IExecuteActionResponse> {
    const response = await this.client.executeAction(request)
    return response
  }
}

export class ActionsApiClientV1 {
  constructor(private readonly client: FetchApiClient) {}

  async getTask<T = unknown>(id: string): Promise<ActionTask<T>> {
    return this.client.get<ActionTask<T>>(`/ui/v1/action-tasks/${id}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
  }

  async enqueue<
    T = unknown,
    TValue extends Record<string, unknown> = Record<string, unknown>,
    TArgs extends Record<string, unknown> = Record<string, unknown>,
  >(input: ActionTaskInput<TValue, TArgs>): Promise<ActionTask<T>> {
    return this.client.post<ActionTask<T>>(`/ui/v1/action-tasks`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    })
  }
}
